import * as React from "react";
import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { alpha, Stack, styled, Switch, Typography } from "@mui/material";
import colors from "../theme/colors";
import fonts from "../theme/fonts";
import {
  getGestureLocalStorage,
  getLegLocalStorage,
  getSetLocalStorage,
  getSoundLocalStorage,
  getX01OrderLocalStorage,
  setGestureLocalStorage,
  setLegLocalStorage,
  setSetLocalStorage,
  setSoundLocalStorage,
  setX01OrderLocalStorage,
} from "../util/localStorage";
import { useNavigate } from "react-router";
import { GAME_TYPE } from "../types/match";
import { X01_ORDER } from "../types/x01";

type Anchor = "right";

const anchor: Anchor = "right";

const LegOptions = [1, 3, 5, 7];
const SetOptions = [3, 5, 7, 9, 11, 13];

interface SideMenuProps {
  menuOpen: boolean;
  setMenuOpen: Function;
  rematchHandler: Function;
  showMultiplayer: boolean;
  handlePlayerChange: Function;
  handleChangeGameType: Function;
  gameType: GAME_TYPE;
}

export default function SideMenu({
  menuOpen,
  setMenuOpen,
  rematchHandler,
  showMultiplayer,
  handlePlayerChange,
  gameType,
}: SideMenuProps) {
  const navigate = useNavigate();
  const [soundOn, setSoundOn] = React.useState(getSoundLocalStorage());
  const [gestureOn, setGestureOn] = React.useState(getGestureLocalStorage());
  const [numberOrder, setNumberOrder] = React.useState(
    getX01OrderLocalStorage()
  );
  const [legs, setLegs] = React.useState(getLegLocalStorage());
  const [sets, setSets] = React.useState(getSetLocalStorage());

  const handleSoundToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSoundLocalStorage(event.target.checked);
    setSoundOn(event.target.checked);
  };

  const handleGestureToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGestureLocalStorage(event.target.checked);
    setGestureOn(event.target.checked);
  };

  const handleNumberOrderToggle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const order = event.target.checked
      ? X01_ORDER.HORIZONTAL
      : X01_ORDER.VERTICAL;
    setNumberOrder(order);
    setX01OrderLocalStorage(order);
  };

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event &&
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }

      setMenuOpen(open);
    };

  const list = () => (
    <Box sx={{ width: 250 }} role="presentation">
      <List>
        <ListItem>
          <ListItemBox>
            <Label>Sound</Label>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography>Off</Typography>
              <PinkSwitch
                checked={soundOn}
                onChange={handleSoundToggle}
                inputProps={{ "aria-label": "controlled" }}
              />
              <Typography>On</Typography>
            </Stack>
          </ListItemBox>
        </ListItem>
        <ListItem>
          <ListItemBox>
            <Label>Gesture Scoring</Label>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography>Off</Typography>
              <PinkSwitch
                checked={gestureOn}
                onChange={handleGestureToggle}
                inputProps={{ "aria-label": "controlled" }}
              />
              <Typography>On</Typography>
            </Stack>
          </ListItemBox>
        </ListItem>
        <ListItem>
          <ListItemBox>
            <Label>Number Order</Label>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography>Vertical</Typography>
              <PinkSwitch
                checked={numberOrder === X01_ORDER.HORIZONTAL}
                onChange={handleNumberOrderToggle}
                inputProps={{ "aria-label": "controlled" }}
              />
              <Typography>Horizontal</Typography>
            </Stack>
          </ListItemBox>
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem>
          <ListItemBox>
            <Label>Legs</Label>
            <NumbersWrapper>
              {LegOptions.map((option) => {
                return (
                  <NumberSelect
                    key={`${option}_legs`}
                    active={legs === option}
                    onClick={() => {
                      setLegLocalStorage(option);
                      setLegs(option);
                    }}
                  >
                    {option}
                  </NumberSelect>
                );
              })}
            </NumbersWrapper>
          </ListItemBox>
        </ListItem>
        <ListItem>
          <ListItemBox>
            <Label>Sets</Label>
            <NumbersWrapper>
              {SetOptions.map((option) => {
                return (
                  <NumberSelect
                    key={`${option}_sets`}
                    active={sets === option}
                    onClick={() => {
                      setSetLocalStorage(option);
                      setSets(option);
                    }}
                  >
                    {option}
                  </NumberSelect>
                );
              })}
            </NumbersWrapper>
          </ListItemBox>
        </ListItem>
      </List>
      {gameType !== GAME_TYPE.NOT_SELECTED && (
        <React.Fragment>
          <Divider />
          <List>
            <ListItem>
              <ListItemBox>
                <ListItemButton
                  onClick={() => {
                    setMenuOpen(false);
                    handlePlayerChange();
                  }}
                >
                  <ListItemText
                    primary={showMultiplayer ? "Multiplayer" : "Bot Game"}
                  />
                </ListItemButton>
              </ListItemBox>
            </ListItem>
            <ListItem>
              <ListItemBox>
                <ListItemButton
                  onClick={() => {
                    setMenuOpen(false);
                    rematchHandler();
                  }}
                >
                  <ListItemText primary={"Restart"} />
                </ListItemButton>
              </ListItemBox>
            </ListItem>
          </List>
        </React.Fragment>
      )}
      <Divider />
      <List>
        <ListItem>
          <ListItemBox>
            <ListItemButton
              onClick={() => {
                setMenuOpen(false);
                navigate("/", { state: { restart: true } });
              }}
            >
              <ListItemText primary={"Home"} />
            </ListItemButton>
          </ListItemBox>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Wrapper>
      <React.Fragment key={anchor}>
        <Drawer
          anchor={anchor}
          open={menuOpen}
          PaperProps={{ elevation: 6 }}
          onClose={toggleDrawer(false)}
          onOpen={toggleDrawer(true)}
        >
          {list()}
        </Drawer>
      </React.Fragment>
    </Wrapper>
  );
}

const Wrapper = styled(Box)({});

const Drawer = styled(SwipeableDrawer)({
  "& .MuiDrawer-paper": {
    backgroundColor: colors.lightBeige,
  },
});

const ListItemBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  width: "100%",
  alignItems: "center",
  paddingTop: "5px",
});

const Label = styled(Typography)({
  display: "flex",
});

const PinkSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: colors.lightGreen,
    "&:hover": {
      backgroundColor: alpha(
        colors.lightGreen,
        theme.palette.action.hoverOpacity
      ),
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: colors.lightGreen,
  },
}));

const NumbersWrapper = styled(Box)({
  display: "flex",
  flexFlow: "row wrap",
  justifyContent: "space-around",
  width: "100%",
});

const NumberSelect = styled(Box)<{ active: boolean }>(({ active }) => ({
  color: active ? colors.darkGreen : colors.lightGreen,
  opacity: active ? 1 : 0.5,
  fontSize: fonts.size.mediumLarge,
  cursor: "pointer",
  width: "20px",
  display: "flex",
  justifyContent: "center",
}));
